import { __awaiter } from 'tslib';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Route } from 'react-router';
import { dataIdFromObject } from '@sketch/graphql-cache';
import { MountHandle } from '@sketch/utils';
import './WorkspaceList/index.js';
import './SettingsForm/index.js';
import { TwoFactorAuthenticationPanel, EmailNotificationsPanel } from '@sketch/user';
import './SessionsManagementPanel/index.js';
import { useModalContext, handleFetchMore, Section, LinkButton } from '@sketch/components';
import './OtherOptionsPanel/index.js';
import './LeaveWorkspaceModal/index.js';
import { useUserProfile, useUserAuthorizations, getIsForInAppPresentation, isSsoAuthorization, getPersonalAuthorization, routes } from '@sketch/modules-common';
import { useToast } from '@sketch/toasts';
import { useUserUpdateMutation, useGetUserSettingsQuery } from '@sketch/gql-types';
import SettingsForm from './SettingsForm/SettingsForm.js';
import WorkspaceList from './WorkspaceList/WorkspaceList.js';
import LeaveWorkspaceModal from './LeaveWorkspaceModal/LeaveWorkspaceModal.js';
import SessionsManagementPanel from './SessionsManagementPanel/SessionsManagementPanel.js';
import { OtherOptionsPanel } from './OtherOptionsPanel/OtherOptionsPanel.js';

// Helpers
const createOptimisticResponse = (user, receiveEmailNotifications) => ({
    __typename: 'RootMutationType',
    userUpdate: {
        __typename: 'UserUpdatePayload',
        errors: [],
        successful: true,
        user: Object.assign(Object.assign({}, user), { receiveEmailNotifications }),
    },
});
const GeneralSettings = () => {
    var _a, _b, _c, _d, _e;
    const { showModal } = useModalContext();
    const { showToast } = useToast();
    const { data: userData } = useUserProfile();
    const { authorizations, hasPersonalAuthorization } = useUserAuthorizations();
    const isForInAppPresentation = getIsForInAppPresentation();
    const [updateUser, { called }] = useUserUpdateMutation({
        redirectErrors: false,
        onCompleted: data => {
            const { errors, successful } = data.userUpdate || {};
            if (successful) {
                showToast('Profile settings updated');
            }
            if ((errors === null || errors === void 0 ? void 0 : errors.length) > 0) {
                const errorMessage = errors.map(error => error === null || error === void 0 ? void 0 : error.message).join('; ');
                showToast(errorMessage, 'negative');
            }
        },
        onError: error => {
            showToast(error.message, 'negative');
        },
    });
    const { data: userSettingsData, fetchMore } = useGetUserSettingsQuery();
    const handleLoadMore = handleFetchMore(fetchMore, ['me', 'workspaceMemberships', 'entries'], {
        after: (_b = (_a = userSettingsData === null || userSettingsData === void 0 ? void 0 : userSettingsData.me) === null || _a === void 0 ? void 0 : _a.workspaceMemberships) === null || _b === void 0 ? void 0 : _b.meta.after,
        dataIdFromObject,
    });
    // Merge the user objects, more precisely get the "newEmail".
    const user = Object.assign(Object.assign({}, userData.me), userSettingsData === null || userSettingsData === void 0 ? void 0 : userSettingsData.me);
    const memberships = ((_d = (_c = userSettingsData === null || userSettingsData === void 0 ? void 0 : userSettingsData.me) === null || _c === void 0 ? void 0 : _c.workspaceMemberships) === null || _d === void 0 ? void 0 : _d.entries) || [];
    const hasMASubscription = memberships.some(membership => {
        var _a, _b, _c;
        return ((_b = (_a = membership === null || membership === void 0 ? void 0 : membership.workspace) === null || _a === void 0 ? void 0 : _a.customer) === null || _b === void 0 ? void 0 : _b.source) === 'macappstore' &&
            ((_c = membership === null || membership === void 0 ? void 0 : membership.workspace) === null || _c === void 0 ? void 0 : _c.userIsOwner);
    });
    const isSignedInWithSso = authorizations.some(isSsoAuthorization);
    const isSsoOnly = !getPersonalAuthorization();
    return (jsxs(Fragment, { children: [jsx(Route, { path: routes.PERSONAL_SETTINGS_UNSUBSCRIBE.template(), render: ({ history }) => (jsx(MountHandle, { onMount: () => __awaiter(void 0, void 0, void 0, function* () {
                        // Prevent calling the mutation again if it's already called (when mounting)
                        // Related to https://github.com/sketch-hq/Cloud/issues/6102#issuecomment-804823199
                        if (called) {
                            return;
                        }
                        user.hasPersonalIdentity &&
                            (yield updateUser({
                                variables: { input: { receiveEmailNotifications: false } },
                                optimisticResponse: createOptimisticResponse(user, false),
                            }));
                        history.replace(routes.PERSONAL_SETTINGS.create({}));
                    }) })) }), jsx(Section, Object.assign({ title: "Personal" }, { children: jsx(SettingsForm, { userName: user.name, userEmail: user.email, userNewEmail: user.newEmail, userAvatarSrc: ((_e = user.avatar) === null || _e === void 0 ? void 0 : _e.large) || user.avatar.small, userHasAvatar: user.hasAvatar, userHasPersonalIdentity: user.hasPersonalIdentity, userHasPersonalAuthorization: hasPersonalAuthorization, isSignedInWithSso: isSignedInWithSso, onUserUpdate: updateUser }) })), !isSsoOnly && (jsx(TwoFactorAuthenticationPanel, { mfaState: user.mfaState, isVerified: user.isVerified })), memberships.length > 0 && (jsx(WorkspaceList, { memberships: memberships, onLoadMore: handleLoadMore, onLeaveWorkspace: membership => {
                    showModal(LeaveWorkspaceModal, {
                        membershipId: membership.identifier,
                        workspace: membership.workspace,
                    });
                }, linkButton: hasPersonalAuthorization &&
                    !hasMASubscription &&
                    !isForInAppPresentation && (jsx(LinkButton, Object.assign({ to: routes.WORKSPACE_CREATE.create({}), variant: "primary", size: "40" }, { children: "Create Workspace\u2026" }))) })), jsx(SessionsManagementPanel, {}), user.hasPersonalIdentity && (jsx(EmailNotificationsPanel, { receiveEmailNotifications: user.receiveEmailNotifications })), jsx(OtherOptionsPanel, { hasPersonalIdentity: user.hasPersonalIdentity })] }));
};

export { GeneralSettings as default };
